import { without } from 'lodash-es';
import pRetry, { type FailedAttemptError } from 'p-retry';
import { isAudioWorkEnabled, type AudioWorkContext } from '../../audioWorkBarrier';
import { getSafeErrorFields, isSessionNotFoundError } from '../../errors';
import {
  getRecordingProgress,
  getRecordingRow,
  isRecordingUploadComplete,
  isRetainedChunk,
  markChunkFailed,
  markChunkUploaded,
  markRecordingAbandoned,
  peekRecordingProgress,
  peekRecordingRow,
  peekRecordingUploadComplete,
  releaseRetainedChunk,
  retainFailedChunk,
} from '../../persistenceV2';
// Direct: the mocked `../../persistenceV2` door omits these predicates in suites
// that only stub the store functions.
import { hasIdempotentChunkDelivery, type RecordingRow } from '../../persistenceV2/types';
import { isMemoryOnlyWriteError } from '../../persistenceV2/writeFence';
import { type SessionChunk } from '../../types';
import { getChunkInfo } from '../lib/getChunkInfo';
import { logStorePersistFailure } from '../lib/logStorePersistFailure';
import { logger } from '../lib/logger';
import { simpleRetry } from '../simpleRetry';
import {
  beginExclusiveUpload,
  chunkUploadKey,
  endExclusiveUpload,
  waitForPersist,
} from './persistRegistry';
import {
  isRecoveryDeferredError,
  isRecoveryUploadTimeoutError,
  RECOVERY_CHUNK_UPLOAD_TIMEOUT_MS,
  RECOVERY_CONNECTIVITY_WAIT_MS,
  uploadBounded,
  waitForConnectivityBounded,
} from './recoveryBounds';
import { pokeRetryDrain } from './retryDrainV2';
import { chunkMeta, requireConfig, type GetState, type SetState } from './storeTypes';

/**
 * `deduped`: the in-flight owner settles the chunk, so no budget is spent here.
 * `deferred`: a recovery dispatch sent nothing — connectivity stayed out past
 * its bound — and the row is untouched.
 */
export type ChunkDispatchOutcome = 'dispatched' | 'deduped' | 'deferred';

/** Recovery replays another page's rows, so only it takes the bounded waits. */
export type ChunkDispatchSource = 'capture' | 'retry-drain' | 'recovery';

const completionChecks = new Map<string, Promise<boolean>>();
const completedRecordings = new Set<string>();

const completionKey = (sessionId: string, recordingId: string) =>
  `${sessionId}|${recordingId}`;

/** Coalesces durable completion checks without coupling one recording to another. */
export const maybeCompleteRecordingV2 = (
  _set: SetState,
  get: GetState,
  key: { sessionId: string; recordingId: string },
  context?: AudioWorkContext
): Promise<boolean> => {
  const id = completionKey(key.sessionId, key.recordingId);
  if (completedRecordings.has(id)) return Promise.resolve(true);

  // Queue another fresh read behind an in-flight check so a terminal transition
  // cannot be hidden by an earlier check that observed the pre-transition row.
  const previous = completionChecks.get(id) ?? Promise.resolve(false);
  const check = previous
    .catch(() => false)
    .then(async () => {
      try {
        if (!isAudioWorkEnabled(context)) return false;
        if (completedRecordings.has(id)) return true;
        // Mirror first, so this page's recordings complete without reading
        // storage; recovery-drained ones fall through to the database.
        const complete = peekRecordingUploadComplete(key);
        if (!(complete ?? (await isRecordingUploadComplete(key)))) return false;
        if (!isAudioWorkEnabled(context)) return false;
        const recording = peekRecordingRow(key) ?? (await getRecordingRow(key));
        if (!recording || !isAudioWorkEnabled(context)) return false;
        const progress = peekRecordingProgress(key) ?? (await getRecordingProgress(key));
        if (!isAudioWorkEnabled(context)) return false;

        completedRecordings.add(id);
        try {
          get().config?.onLastChunkProcessed?.(
            key.recordingId,
            recording.useTranscriptionServiceV2,
            {
              sessionId: recording.sessionId,
              uploadTransport: recording.uploadTransport,
              lastChunkIndex: recording.lastChunkIndex,
              gaps: { failed: progress.failed, abandoned: progress.abandoned },
            }
          );
        } catch (err) {
          logger.error({
            event_type: 'RECORDING_COMPLETION_CALLBACK_FAILED',
            details: { ...key, ...getSafeErrorFields(err) },
          });
        }
        void get().cleanupRecordingsTask();
        return true;
      } catch (err) {
        logger.error({
          event_type: 'RECORDING_COMPLETION_CHECK_FAILED',
          details: { ...key, ...getSafeErrorFields(err) },
        });
        return false;
      }
    });
  completionChecks.set(id, check);
  const cleanup = () => {
    if (completionChecks.get(id) === check) completionChecks.delete(id);
  };
  void check.then(cleanup, cleanup);
  return check;
};

/** Updates durable status after upload success, 404, or retry exhaustion. */
export const processChunkV2 = async (
  set: SetState,
  get: GetState,
  chunk: SessionChunk,
  useTranscriptionServiceV2: boolean,
  context?: AudioWorkContext,
  source: ChunkDispatchSource = 'capture'
): Promise<ChunkDispatchOutcome> => {
  // Chokepoint dedupe: capture, recovery re-scans, and hydrate retries can
  // all dispatch the same still-`pending` row — only one upload may run.
  const uploadKey = chunkUploadKey(chunk);
  if (!beginExclusiveUpload(uploadKey)) {
    logger.info({
      event_type: 'CHUNK_UPLOAD_DEDUPED_IN_FLIGHT',
      details: getChunkInfo(chunk),
    });
    return 'deduped';
  }
  try {
    return await runProcessChunkV2(
      set,
      get,
      chunk,
      useTranscriptionServiceV2,
      context,
      source
    );
  } finally {
    endExclusiveUpload(uploadKey);
  }
};

const runProcessChunkV2 = async (
  set: SetState,
  get: GetState,
  chunk: SessionChunk,
  useTranscriptionServiceV2: boolean,
  context: AudioWorkContext | undefined,
  source: ChunkDispatchSource
): Promise<'dispatched' | 'deferred'> => {
  const { id } = chunk;
  const { waitForConnectivity } = get();
  const chunkInfo = { ...getChunkInfo(chunk) };
  const bounds =
    source === 'recovery'
      ? {
          connectivity: RECOVERY_CONNECTIVITY_WAIT_MS,
          upload: RECOVERY_CHUNK_UPLOAD_TIMEOUT_MS,
        }
      : undefined;
  let attemptedRow: RecordingRow | undefined;

  try {
    const config = requireConfig(get);
    set((s) => ({ processingChunksIds: [...s.processingChunksIds, id] }));
    logger.debug({ event_type: 'PROCESS_CHUNK_START', details: chunkInfo });

    await pRetry(
      async () => {
        context?.throwIfStale();
        await waitForConnectivityBounded(
          waitForConnectivity,
          context?.signal,
          bounds?.connectivity
        );
        context?.throwIfStale();
        // Mirror first: the pin transaction updates it, so a ticket refresh is
        // visible without a read. On a database miss the attempt still fails —
        // a pinned delivery never mixes transports.
        const row = peekRecordingRow(chunk) ?? (await getRecordingRow(chunk));
        if (!row) throw new Error('recording row missing for ledger chunk upload');
        attemptedRow = row;
        context?.throwIfStale();
        await uploadBounded(
          (signal) =>
            config.uploadChunk(
              chunk,
              useTranscriptionServiceV2,
              signal,
              row.uploadTransport
            ),
          context?.signal,
          bounds?.upload
        );
      },
      {
        retries: 4,
        signal: context?.signal,
        // A timed-out request may still land; only an idempotent delivery re-sends it.
        shouldRetry: (error: FailedAttemptError) =>
          !isSessionNotFoundError(error) &&
          !isRecoveryDeferredError(error) &&
          !(
            isRecoveryUploadTimeoutError(error) &&
            attemptedRow !== undefined &&
            !hasIdempotentChunkDelivery(attemptedRow)
          ),
        onFailedAttempt: (err) => {
          // A deferral sent nothing, so there was no attempt to report.
          if (isRecoveryDeferredError(err)) return;
          logger.info({
            event_type: 'PROCESS_CHUNK_FAILED_ATTEMPT',
            details: {
              chunkInfo,
              ...getSafeErrorFields(err),
              attemptNumber: err.attemptNumber,
              retriesLeft: err.retriesLeft,
            },
          });
        },
      }
    );

    try {
      if (isAudioWorkEnabled(context)) {
        await simpleRetry(() =>
          markChunkUploaded(chunkMeta(chunk), { deleteBlob: !config.retainData })
        );
      }
    } catch (err) {
      // A memory-only refusal is the start's storage-less event repeated once
      // per chunk: expected, and loud enough at error level to bury real faults.
      logStorePersistFailure(
        {
          event_type: 'MARK_CHUNK_UPLOADED_ERROR',
          details: { chunkInfo, ...getSafeErrorFields(err) },
        },
        { memoryOnly: isMemoryOnlyWriteError(err) }
      );
    }

    // The upload landed, so the buffered copy is a duplicate now — pure
    // in-memory bookkeeping, with no liveness to wait on.
    releaseRetainedChunk(chunk);

    if (!isAudioWorkEnabled(context)) return 'dispatched';
    set((s) => ({ processingChunksIds: without(s.processingChunksIds, id) }));

    await maybeCompleteRecordingV2(set, get, chunk, context);

    logger.debug({ event_type: 'PROCESS_CHUNK_DONE', details: chunkInfo });
    // A success proves the backend is reachable — retry anything retained.
    pokeRetryDrain(set, get, 'upload-success');
  } catch (err) {
    if (!isAudioWorkEnabled(context)) return 'dispatched';
    if (isRecoveryDeferredError(err)) {
      // Nothing was sent, so nothing is marked and no budget is spent.
      set((s) => ({ processingChunksIds: without(s.processingChunksIds, id) }));
      logger.info({ event_type: 'RECOVERY_CHUNK_DEFERRED_OFFLINE', details: chunkInfo });
      return 'deferred';
    }
    // A retained chunk failing again is already-known news: only drain replays
    // dispatch one, and drains run on every upload success, stop, note
    // generation and connectivity recovery — mid-capture included.
    const knownFailed = isRetainedChunk(chunk);
    // A recovered row from an earlier consult must not flip the live one to ERROR.
    const liveRecording = chunk.recordingId === get().recordingId;
    if (!knownFailed && liveRecording) set({ status: 'ERROR' });
    set((s) => ({ processingChunksIds: without(s.processingChunksIds, id) }));

    logger.error({
      event_type: 'PROCESS_CHUNK_FAILED',
      details: { ...getSafeErrorFields(err), chunkInfo, source },
    });

    // Preserve persist-before-terminal ordering, even if IndexedDB is slow.
    const persistWait = await waitForPersist(chunk);
    if (!isAudioWorkEnabled(context)) return 'dispatched';
    if (persistWait === 'rejected' || persistWait === 'timed-out') {
      logger.warn({
        event_type: 'CHUNK_PERSIST_WAIT_INCOMPLETE',
        details: { ...chunkInfo, outcome: persistWait },
      });
    }

    let terminalStatePersisted = false;
    try {
      if (isAudioWorkEnabled(context)) {
        if (isSessionNotFoundError(err)) {
          await simpleRetry(() => markRecordingAbandoned(chunk));
        } else {
          // Retained BEFORE the status write: markChunkFailed can itself fail
          // on the storage this exists to survive.
          await retainFailedChunk(chunk);
          await simpleRetry(() => markChunkFailed(chunkMeta(chunk)));
        }
        terminalStatePersisted = true;
      }
    } catch (persistErr) {
      // The mirror already holds the terminal state, so a memory-only refusal
      // settles the chunk as surely as a durable write; treating it as unsettled
      // strands completion on a late-failing last chunk.
      const memoryOnly = isMemoryOnlyWriteError(persistErr);
      if (memoryOnly) terminalStatePersisted = true;
      logStorePersistFailure(
        {
          event_type: 'MARK_CHUNK_FAILED_ERROR',
          details: { chunkInfo, ...getSafeErrorFields(persistErr) },
        },
        { memoryOnly }
      );
    }

    if (terminalStatePersisted) {
      await maybeCompleteRecordingV2(set, get, chunk, context);
    }

    if (isAudioWorkEnabled(context) && !knownFailed && liveRecording) {
      get().config?.onError?.(err);
    }
  }
  return 'dispatched';
};

/** Treats failed chunks as settled so note generation can continue. */
export const getIsLastChunkV2 = async (_get: GetState, chunk: SessionChunk) => {
  try {
    // Mirror first, so a memory-only recording can still settle; an undecided
    // mirror falls through to the database rather than answering `false`.
    return peekRecordingUploadComplete(chunk) ?? (await isRecordingUploadComplete(chunk));
  } catch (err) {
    logger.warn({
      event_type: 'GET_IS_LAST_CHUNK_V2_FAILED',
      details: { ...getChunkInfo(chunk), ...getSafeErrorFields(err) },
    });
    return false;
  }
};

/** Test-only. */
export const __resetCompletionChecksForTests = (): void => {
  completionChecks.clear();
  completedRecordings.clear();
};
